import React, { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import {
  BadgeDollarSign,
  BarChart3,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  Info as InfoIcon,
  Image as ImageIcon,
  LayoutDashboard,
  ListPlus,
  LogOut,
  PackageCheck,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import "./styles.css";

const BRAND_LOGO_SRC = "/static/so-atlanta-logo-black.png";

type PartnerUser = {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  status: string;
  createdAt: string;
  lastLoginAt: string | null;
};

type BusinessSummary = {
  roleId: string;
  role: string;
  status: "PENDING" | "APPROVED" | "REVOKED";
  approvedAt: string | null;
  business: BusinessCard;
};

type BusinessCard = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  phone: string | null;
  websiteUrl: string | null;
  instagramUrl: string | null;
  tiktokUrl: string | null;
  shopifyUrl: string | null;
  imageUrl: string | null;
  logoUrl: string | null;
  listingType: "PLACE" | "SERVICE";
  searchable: boolean;
  dropMeOffEligible: boolean;
  visitIntent: string | null;
  serviceArea: string | null;
  serviceBookingUrl: string | null;
  serviceContactMethod: string | null;
  servicePortfolioUrl: string | null;
  noAddressReason: string | null;
  partnerStatus: string | null;
  updatedAt: string;
  lastVerifiedAt: string | null;
  category: { id: string; name: string; slug: string } | null;
  neighborhood: { id: string; name: string; slug: string } | null;
  products: Array<{
    id: string;
    productHandle: string;
    productTitle: string | null;
    collectionHandle: string | null;
    sortOrder: number;
  }>;
};

type ClaimRequest = {
  id: string;
  status: string;
  businessSlug: string;
  businessName: string;
  createdAt: string;
  reviewedAt: string | null;
  business: { id: string; slug: string; name: string; shopifyUrl: string | null; partnerStatus: string | null } | null;
};

type ListingRequest = {
  id: string;
  status: string;
  businessName: string;
  category: string | null;
  neighborhood: string | null;
  createdAt: string;
  reviewedAt: string | null;
  matchedBusiness: { id: string; slug: string; name: string; shopifyUrl: string | null } | null;
};

type ChangeRequest = {
  id: string;
  requestType: "PROFILE_UPDATE" | "PRODUCT_LINK" | "CAMPAIGN" | "OFFER";
  status: string;
  payload: Record<string, unknown>;
  createdAt: string;
  reviewedAt: string | null;
  business: { id: string; slug: string; name: string; shopifyUrl: string | null } | null;
};

type DashboardSummary = {
  user: PartnerUser;
  businesses: BusinessSummary[];
  claimRequests: ClaimRequest[];
  listingRequests: ListingRequest[];
  changeRequests: ChangeRequest[];
  adAccount: AdAccount;
  analytics: {
    spendCents: number;
    impressions: number;
    clicks: number;
    saves: number;
    offerRedemptions: number;
    status: string;
  };
};

type AdAccount = {
  balanceCents: number;
  heldCents: number;
  committedCents: number;
  spentCents: number;
  availableCents: number;
};

type MarketplaceState = {
  currency: "USD";
  settings: {
    featuredWeeklyRateCents: number;
    featuredWeeklySlotCap: number;
    packagingMonthlyRateCents: number;
    packagingMonthlySlotCap: number;
    cpmMinBudgetCents: number;
    cpmMaxConcurrentCampaigns: number;
    homepageMidpageCpmCents: number;
    businessPageCpmCents: number;
    packagingCutoffDay: number;
  };
  inventory: {
    featuredWeeks: Array<{ startDate: string; endDate: string; spotsLeft: number; slotCap: number }>;
    packagingMonths: Array<{ month: string; spotsLeft: number; slotCap: number }>;
  };
  businessRules: Array<{
    businessId: string;
    featuredWeekStartDates: string[];
    packagingMonths: string[];
    cpmRequestCount: number;
  }>;
  adAccount: AdAccount;
};

type CampaignProductType = "FEATURED_BUSINESS" | "HOMEPAGE_MIDPAGE_CPM" | "BUSINESS_PROFILE_CPM" | "PACKAGING_INSERT";

type CampaignCartItem = {
  localId: string;
  productType: CampaignProductType;
  businessId: string;
  businessName: string;
  title: string;
  costCents: number;
  details: string[];
  startDate?: string;
  weeks?: number;
  month?: string;
  approvedMediaRequestId?: string;
  budgetCents?: number;
  endDate?: string;
  targetStartHour?: number;
  targetEndHour?: number;
};

type SetupTask = {
  id: string;
  title: string;
  description: string;
  complete: boolean;
  actionLabel?: string;
  actionPath?: string;
};

type Catalog = {
  categories: Array<{ id: string; name: string; slug: string }>;
  neighborhoods: Array<{ id: string; name: string; slug: string }>;
};

type SearchBusiness = {
  id: string;
  slug: string;
  name: string;
  partnerStatus: string | null;
  shopifyUrl: string | null;
  category: { name: string; slug: string } | null;
  neighborhood: { name: string; slug: string } | null;
};

type PricingSlot = {
  startsAt: string;
  placement: string;
  demandMultiplier: number;
  cpmCents: number;
  cpcCents: number;
  inventory: "available";
};

type ApiOptions = Omit<RequestInit, "body"> & {
  body?: Record<string, unknown>;
};

const routes = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/list-your-business", label: "Get Listed", icon: ListPlus },
  { path: "/businesses", label: "Listings", icon: Building2 },
  { path: "/media", label: "Media", icon: ImageIcon },
  { path: "/campaigns", label: "Campaigns", icon: BadgeDollarSign },
  { path: "/billing", label: "Billing", icon: CircleDollarSign },
  { path: "/settings", label: "Settings", icon: Settings },
] as const;

const adPlacementSpecs = [
  { value: "homepage_midpage", label: "Homepage mid-page ad", size: "1200 x 628" },
  { value: "business_page", label: "Business profile page ad", size: "1200 x 628" },
  { value: "packaging_insert", label: "Packaging insert", size: "1200 x 1800" },
] as const;

const campaignProductLabels: Record<CampaignProductType, string> = {
  FEATURED_BUSINESS: "Featured Business",
  HOMEPAGE_MIDPAGE_CPM: "Homepage mid-page ad",
  BUSINESS_PROFILE_CPM: "Business profile page ad",
  PACKAGING_INSERT: "Packaging insert",
};

const cpmPlacementForProduct: Record<"HOMEPAGE_MIDPAGE_CPM" | "BUSINESS_PROFILE_CPM", string> = {
  HOMEPAGE_MIDPAGE_CPM: "homepage_midpage",
  BUSINESS_PROFILE_CPM: "business_page",
};

async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers ?? {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  const payload = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) {
    throw new Error(payload.error || "Request failed.");
  }
  return payload as T;
}

async function apiForm<T>(path: string, form: FormData): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    body: form,
  });

  const payload = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) {
    throw new Error(payload.error || "Request failed.");
  }
  return payload as T;
}

function currentPath(): string {
  const path = window.location.pathname;
  if (path === "/" || path === "") return "/dashboard";
  return path;
}

function pushPath(path: string): void {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function messageFromError(err: unknown): string {
  return err instanceof Error ? err.message : "Request failed.";
}

function formatDate(value: string | null): string {
  if (!value) return "Not reviewed";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

function money(cents: number): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(cents / 100);
}

function centsFromDollarInput(value: string): number {
  const parsed = Number.parseFloat(value.replace(/[$,]/g, ""));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

function formatHour(hour: number): string {
  return new Intl.DateTimeFormat(undefined, { hour: "numeric" }).format(new Date(2026, 0, 1, hour));
}

function formatCompactHour(hour: number): string {
  const normalizedHour = ((hour % 24) + 24) % 24;
  const suffix = normalizedHour >= 12 ? "pm" : "am";
  const displayHour = normalizedHour % 12 || 12;
  return `${displayHour}${suffix}`;
}

function formatDateKey(value: string): string {
  if (!value) return "";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${value}T00:00:00.000Z`));
}

function formatMonthKey(value: string): string {
  if (!value) return "";
  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${value}-01T00:00:00.000Z`));
}

function formRecord(form: HTMLFormElement): Record<string, string> {
  const output: Record<string, string> = {};
  new FormData(form).forEach((value, key) => {
    if (typeof value === "string") {
      const cleaned = value.trim();
      if (cleaned) output[key] = cleaned;
    }
  });
  return output;
}

function payloadRecord(payload: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return payload && typeof payload === "object" && !Array.isArray(payload) ? payload : {};
}

function payloadText(payload: Record<string, unknown> | null | undefined, key: string): string {
  const value = payloadRecord(payload)[key];
  return typeof value === "string" ? value : "";
}

function isAdMediaRequest(item: ChangeRequest): boolean {
  return item.requestType === "CAMPAIGN" && payloadText(item.payload, "requestKind") === "AD_MEDIA";
}

function isAdProductRequest(item: ChangeRequest): boolean {
  return item.requestType === "CAMPAIGN" && payloadText(item.payload, "requestKind") === "AD_PRODUCT";
}

function isApprovedAdMediaRequest(item: ChangeRequest): boolean {
  return isAdMediaRequest(item) && item.status === "APPROVED";
}

function mediaRequestTitle(item: ChangeRequest): string {
  return payloadText(item.payload, "assetTitle") || item.business?.name || "Ad media";
}

function placementLabel(value: string): string {
  return adPlacementSpecs.find((item) => item.value === value)?.label ?? value;
}

function requestProductTitle(item: ChangeRequest): string {
  const payload = payloadRecord(item.payload);
  const productType = payloadText(payload, "productType") as CampaignProductType;
  return campaignProductLabels[productType] ?? typeLabel(item.requestType);
}

function requestProductDetails(item: ChangeRequest): string {
  const payload = payloadRecord(item.payload);
  const productType = payloadText(payload, "productType");
  if (productType === "FEATURED_BUSINESS") {
    return `${payload.weeks ?? 1} week${payload.weeks === 1 ? "" : "s"} from ${formatDateKey(payloadText(payload, "startDate"))}`;
  }
  if (productType === "PACKAGING_INSERT") {
    return formatMonthKey(payloadText(payload, "month"));
  }
  if (productType === "HOMEPAGE_MIDPAGE_CPM" || productType === "BUSINESS_PROFILE_CPM") {
    const budget = typeof payload.budgetCents === "number" ? money(payload.budgetCents) : "Budget held";
    return `${budget} / ${payloadText(payload, "startDate")} to ${payloadText(payload, "endDate")}`;
  }
  return formatDate(item.createdAt);
}

function setupTasksForState(state: AppState): SetupTask[] {
  const mediaRequests = state.summary.changeRequests.filter(isAdMediaRequest);
  const approvedMedia = mediaRequests.filter(isApprovedAdMediaRequest);
  const adRequests = state.summary.changeRequests.filter(isAdProductRequest);
  const hasApprovedBusiness = state.approvedBusinesses.length > 0;
  const hasFundedAccount =
    state.summary.adAccount.balanceCents > 0 ||
    state.summary.adAccount.heldCents > 0 ||
    state.summary.adAccount.committedCents > 0 ||
    state.summary.adAccount.availableCents > 0;
  const firstBusinessName = state.approvedBusinesses[0]?.business.name ?? "your listing";
  const campaignAction =
    !approvedMedia.length ? { label: "Submit media", path: "/media" } :
      !hasFundedAccount ? { label: "Fund account", path: "/billing" } :
        { label: "Build campaign", path: "/campaigns" };

  return [
    {
      id: "business",
      title: "Listing approved",
      description: hasApprovedBusiness
        ? `${firstBusinessName} is connected to this partner profile.`
        : "An approved listing is required before media or campaigns can be submitted.",
      complete: hasApprovedBusiness,
      actionLabel: hasApprovedBusiness ? undefined : "Find listing",
      actionPath: hasApprovedBusiness ? undefined : "/businesses",
    },
    {
      id: "funding",
      title: "Add ad credits",
      description: "Campaigns are prepaid from your ad account. Ad credits are non-refundable.",
      complete: hasFundedAccount,
      actionLabel: hasFundedAccount ? "View billing" : "Fund account",
      actionPath: "/billing",
    },
    {
      id: "media-submit",
      title: "Submit ad media",
      description: "Upload finished creative or request So Atlanta creative production for $40 if you can provide high-quality images.",
      complete: mediaRequests.length > 0,
      actionLabel: mediaRequests.length ? "View media" : "Submit media",
      actionPath: "/media",
    },
    {
      id: "media-approval",
      title: "Get media approved",
      description: "Campaign placements can only use creative approved for that exact placement.",
      complete: approvedMedia.length > 0,
      actionLabel: mediaRequests.length ? "Check media" : "Submit media",
      actionPath: "/media",
    },
    {
      id: "campaign",
      title: "Submit first ad request",
      description: "Choose a feature, CPM placement, or packaging insert and submit the cart for review.",
      complete: adRequests.length > 0,
      actionLabel: adRequests.length ? "View requests" : campaignAction.label,
      actionPath: adRequests.length ? "/campaigns" : campaignAction.path,
    },
  ];
}

function shouldShowSetupChecklist(state: AppState): boolean {
  const tasks = setupTasksForState(state);
  const hasApprovedBusiness = state.approvedBusinesses.length > 0;
  return hasApprovedBusiness && tasks.some((task) => !task.complete);
}

function statusTone(status: string): string {
  if (status === "APPROVED") return "good";
  if (status === "REJECTED" || status === "SPAM" || status === "REVOKED") return "muted";
  if (status === "RESOLVED") return "good";
  return "pending";
}

function listingTypeLabel(type: BusinessCard["listingType"] | undefined): string {
  if (type === "SERVICE") return "Service";
  return "Place";
}

function App() {
  const [path, setPath] = useState(currentPath());
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<PartnerUser | null>(null);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [catalog, setCatalog] = useState<Catalog>({ categories: [], neighborhoods: [] });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function reloadDashboard(): Promise<void> {
    const [sessionPayload, summaryPayload, catalogPayload] = await Promise.all([
      api<{ user: PartnerUser }>("/partner-api/session"),
      api<DashboardSummary>("/partner-api/summary"),
      api<Catalog>("/partner-api/catalog"),
    ]);
    setUser(sessionPayload.user);
    setSummary(summaryPayload);
    setCatalog(catalogPayload);
  }

  useEffect(() => {
    const onPop = () => setPath(currentPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    reloadDashboard()
      .catch(() => {
        setUser(null);
        setSummary(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const approvedBusinesses = useMemo(
    () => summary?.businesses.filter((item) => item.status === "APPROVED") ?? [],
    [summary]
  );
  const hasLinkedBusiness = (summary?.businesses.length ?? 0) > 0;
  const visibleRoutes = useMemo(
    () => routes.filter((route) => route.path !== "/list-your-business" || !hasLinkedBusiness),
    [hasLinkedBusiness]
  );

  useEffect(() => {
    if (hasLinkedBusiness && path === "/list-your-business") {
      pushPath("/dashboard");
    }
  }, [hasLinkedBusiness, path]);

  async function handleLogout(): Promise<void> {
    await api<{ ok: true }>("/partner-api/auth/logout", { method: "POST" });
    setUser(null);
    setSummary(null);
    pushPath("/dashboard");
  }

  if (loading) {
    return <ScreenCenter>Loading partner dashboard</ScreenCenter>;
  }

  if (!user || !summary) {
    return (
      <AuthScreen
        onAuthenticated={async () => {
          setLoading(true);
          await reloadDashboard();
          setLoading(false);
        }}
      />
    );
  }

  const appState: AppState = {
    path,
    user,
    summary,
    catalog,
    approvedBusinesses,
    notice,
    error,
    setNotice,
    setError,
    reloadDashboard,
  };

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <BrandLockup label="Partners" />
        <nav className="nav-list">
          {visibleRoutes.map((item) => {
            const Icon = item.icon;
            const active = path === item.path;
            return (
              <button
                key={item.path}
                className={`nav-link ${active ? "active" : ""}`}
                type="button"
                onClick={() => pushPath(item.path)}
                title={item.label}
              >
                <Icon size={18} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <button className="logout" type="button" onClick={() => void handleLogout()}>
          <LogOut size={16} />
          <span>Sign out</span>
        </button>
      </aside>
      <main className="main">
        <div className="topbar">
          <div>
            <h1>{titleForPath(path)}</h1>
            <span>{user.email}</span>
          </div>
          {hasLinkedBusiness ? (
            <button className="secondary-button" type="button" onClick={() => pushPath("/billing")}>
              <CircleDollarSign size={16} />
              Fund ad account
            </button>
          ) : (
            <button className="secondary-button" type="button" onClick={() => pushPath("/claim")}>
              <ShieldCheck size={16} />
              Claim business
            </button>
          )}
        </div>
        {notice ? <Banner tone="success" onClose={() => setNotice(null)}>{notice}</Banner> : null}
        {error ? <Banner tone="error" onClose={() => setError(null)}>{error}</Banner> : null}
        <RouteView state={appState} />
      </main>
    </div>
  );
}

type AppState = {
  path: string;
  user: PartnerUser;
  summary: DashboardSummary;
  catalog: Catalog;
  approvedBusinesses: BusinessSummary[];
  notice: string | null;
  error: string | null;
  setNotice: (value: string | null) => void;
  setError: (value: string | null) => void;
  reloadDashboard: () => Promise<void>;
};

function RouteView({ state }: { state: AppState }) {
  if (state.path === "/claim") return <ClaimPage state={state} />;
  if (state.path === "/list-your-business" && state.summary.businesses.length) return <DashboardPage state={state} />;
  if (state.path === "/list-your-business") return <ListingPage state={state} />;
  if (state.path === "/businesses") return <BusinessesPage state={state} />;
  if (state.path === "/media") return <MediaPage state={state} />;
  if (state.path === "/campaigns") return <CampaignsPage state={state} />;
  if (state.path === "/billing") return <BillingPage state={state} />;
  if (state.path === "/settings") return <SettingsPage state={state} />;
  return <DashboardPage state={state} />;
}

function titleForPath(path: string): string {
  if (path === "/claim") return "Claim";
  const item = routes.find((route) => route.path === path);
  return item?.label ?? "Dashboard";
}

function AuthScreen({ onAuthenticated }: { onAuthenticated: () => Promise<void> }) {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const values = formRecord(event.currentTarget);
    try {
      await api<{ user: PartnerUser }>(mode === "signup" ? "/partner-api/auth/signup" : "/partner-api/auth/login", {
        method: "POST",
        body: values,
      });
      await onAuthenticated();
    } catch (err) {
      setError(messageFromError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-layout">
      <div className="auth-panel">
        <BrandLockup label="Partners" variant="auth" />
        <div className="segment">
          <button className={mode === "login" ? "selected" : ""} type="button" onClick={() => setMode("login")}>
            Sign in
          </button>
          <button className={mode === "signup" ? "selected" : ""} type="button" onClick={() => setMode("signup")}>
            Create account
          </button>
        </div>
        {error ? <div className="form-error">{error}</div> : null}
        <form className="form-grid" onSubmit={(event) => void submit(event)}>
          {mode === "signup" ? (
            <>
              <Field label="Name" name="name" autoComplete="name" />
              <Field label="Phone" name="phone" autoComplete="tel" />
            </>
          ) : null}
          <Field label="Email" name="email" type="email" autoComplete="email" required />
          <Field label="Password" name="password" type="password" autoComplete={mode === "signup" ? "new-password" : "current-password"} required />
          <button className="primary-button wide" type="submit" disabled={busy}>
            {busy ? "Submitting" : mode === "signup" ? "Create account" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

function DashboardPage({ state }: { state: AppState }) {
  const { summary } = state;
  const pendingClaims = summary.claimRequests.filter((item) => item.status === "NEW" || item.status === "TRIAGED").length;
  const pendingListings = summary.listingRequests.filter((item) => item.status === "NEW" || item.status === "TRIAGED").length;
  const pendingChanges = summary.changeRequests.filter((item) => item.status === "NEW" || item.status === "TRIAGED").length;
  const approvedBusinessCount = summary.businesses.filter((item) => item.status === "APPROVED").length;

  return (
    <div className="page-grid">
      <div className="metrics-grid">
        <Metric label="Businesses" value={approvedBusinessCount.toString()} icon={<Building2 size={18} />} />
        <Metric label="Pending claims" value={pendingClaims.toString()} icon={<ShieldCheck size={18} />} />
        <Metric label="Pending requests" value={pendingChanges.toString()} icon={<ClipboardCheck size={18} />} />
        <Metric label="Spend" value={money(summary.analytics.spendCents)} icon={<CircleDollarSign size={18} />} />
        <Metric label="Impressions" value={summary.analytics.impressions.toLocaleString()} icon={<BarChart3 size={18} />} />
        <Metric label="Clicks" value={summary.analytics.clicks.toLocaleString()} icon={<BadgeDollarSign size={18} />} />
      </div>

      <AdAccountPanel state={state} />

      {shouldShowSetupChecklist(state) ? <SetupChecklist state={state} /> : null}

      <section className="panel">
        <PanelHeader title="Active Businesses" action={<SmallAction label="Manage" path="/businesses" />} />
        <div className="table-list">
          {summary.businesses.length ? (
            summary.businesses.map((item) => <BusinessRow key={item.roleId} item={item} />)
          ) : (
            <EmptyState action="Claim a business" path="/claim" text="No approved businesses yet." />
          )}
        </div>
      </section>

      <section className="panel">
        <PanelHeader title="Requests" />
        <RequestList
          items={[
            ...summary.claimRequests.map((item) => ({
              id: item.id,
              title: item.businessName,
              type: "Claim",
              status: item.status,
              date: item.createdAt,
            })),
            ...summary.listingRequests.map((item) => ({
              id: item.id,
              title: item.businessName,
              type: "Listing",
              status: item.status,
              date: item.createdAt,
            })),
            ...summary.changeRequests.map((item) => ({
              id: item.id,
              title: isAdProductRequest(item) ? requestProductTitle(item) : item.business?.name ?? item.requestType,
              type: isAdMediaRequest(item) ? "Media" : isAdProductRequest(item) ? "Ad" : typeLabel(item.requestType),
              status: item.status,
              date: item.createdAt,
            })),
          ].slice(0, 8)}
        />
        {pendingListings ? <p className="fine-print">{pendingListings} listing request pending.</p> : null}
      </section>
    </div>
  );
}

function AdAccountPanel({ state }: { state: AppState }) {
  const campaignRequests = state.summary.changeRequests.filter((item) => item.requestType === "CAMPAIGN" && !isAdMediaRequest(item));
  const pendingCampaigns = campaignRequests.filter((item) => item.status === "NEW" || item.status === "TRIAGED").length;
  const linkedBusiness = state.summary.businesses[0];
  const approvedBusiness = state.approvedBusinesses[0];
  const statusLabel = pendingCampaigns ? "Budget requested" : approvedBusiness ? "Needs budget" : "Business access needed";
  const title = pendingCampaigns
    ? "Budget request in review"
    : approvedBusiness
      ? `Turn on ads for ${approvedBusiness.business.name}`
      : linkedBusiness
        ? "Ads unlock after approval"
        : "Connect a business to start ads";

  return (
    <section className="panel ad-account-panel">
      <div className="ad-account-copy">
        <div className="ad-account-kicker">
          <BadgeDollarSign size={18} />
          <span>Ad Account</span>
        </div>
        <h2>{title}</h2>
        <p>Set a daily campaign budget, then fund the prepaid account before delivery starts.</p>
      </div>
      <div className="ad-account-status">
        <div>
          <span>Funding</span>
          <strong>Prepaid</strong>
        </div>
        <div>
          <span>Status</span>
          <strong>{statusLabel}</strong>
        </div>
        <div>
          <span>Available</span>
          <strong>{money(state.summary.adAccount.availableCents)}</strong>
        </div>
      </div>
      <div className="ad-account-actions">
        <button className="primary-button" type="button" onClick={() => pushPath("/campaigns")} disabled={!approvedBusiness}>
          <BadgeDollarSign size={16} />
          Set ad budget
        </button>
        <button className="secondary-button" type="button" onClick={() => pushPath("/billing")}>
          <CircleDollarSign size={16} />
          Fund account
        </button>
      </div>
    </section>
  );
}

function SetupChecklist({ state, className = "" }: { state: AppState; className?: string }) {
  const tasks = setupTasksForState(state);
  const completedCount = tasks.filter((task) => task.complete).length;
  const activeTaskId = tasks.find((task) => !task.complete)?.id;
  const progress = `${Math.round((completedCount / tasks.length) * 360)}deg`;

  return (
    <section className={`panel setup-checklist ${className}`.trim()}>
      <div className="setup-checklist-count">
        <span
          className={`setup-progress ${completedCount === tasks.length ? "complete" : ""}`}
          style={{ "--setup-progress": progress } as React.CSSProperties}
          aria-hidden="true"
        >
          {completedCount === tasks.length ? <CheckCircle2 size={15} /> : null}
        </span>
        <strong>{completedCount} of {tasks.length} tasks complete</strong>
      </div>
      <div className="setup-checklist-intro">
        <h2>Launch your first ad</h2>
        <p>Finish these setup steps to move from an approved business to an ad request ready for review.</p>
      </div>
      <div className="setup-task-list">
        {tasks.map((task) => (
          <article
            className={`setup-task ${task.complete ? "complete" : ""} ${task.id === activeTaskId ? "active" : ""}`}
            key={task.id}
          >
            <span className="setup-task-status" aria-hidden="true">
              {task.complete ? <CheckCircle2 size={17} /> : null}
            </span>
            <div className="setup-task-body">
              <strong>{task.title}</strong>
              <p>{task.description}</p>
            </div>
            {!task.complete && task.actionLabel && task.actionPath ? (
              <button
                className={task.id === activeTaskId ? "primary-button" : "secondary-button"}
                type="button"
                onClick={() => pushPath(task.actionPath as string)}
              >
                {task.actionLabel}
              </button>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

function ClaimPage({ state }: { state: AppState }) {
  const querySlug = new URLSearchParams(window.location.search).get("business") ?? "";
  const [search, setSearch] = useState(querySlug);
  const [selectedSlug, setSelectedSlug] = useState(querySlug);
  const [results, setResults] = useState<SearchBusiness[]>([]);
  const [busy, setBusy] = useState(false);

  async function runSearch(event?: FormEvent<HTMLFormElement>): Promise<void> {
    event?.preventDefault();
    if (search.trim().length < 2) return;
    try {
      const payload = await api<{ businesses: SearchBusiness[] }>(`/partner-api/businesses/search?q=${encodeURIComponent(search.trim())}`);
      setResults(payload.businesses);
    } catch (err) {
      state.setError(messageFromError(err));
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    state.setError(null);
    try {
      const values = formRecord(event.currentTarget);
      await api<{ ok: true }>("/partner-api/claim-requests", {
        method: "POST",
        body: { ...values, businessSlug: selectedSlug || values.businessSlug },
      });
      state.setNotice("Claim request submitted for review.");
      await state.reloadDashboard();
    } catch (err) {
      state.setError(messageFromError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="two-column wide-left">
      {shouldShowSetupChecklist(state) ? <SetupChecklist state={state} className="span-two-panel" /> : null}

      <section className="panel">
        <PanelHeader title="Find Business" />
        <form className="search-row" onSubmit={(event) => void runSearch(event)}>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Business name or slug" />
          <button className="secondary-button" type="submit">
            <Search size={16} />
            Search
          </button>
        </form>
        <div className="result-list">
          {results.map((business) => (
            <button
              className={`result-row ${selectedSlug === business.slug ? "selected" : ""}`}
              key={business.id}
              type="button"
              onClick={() => setSelectedSlug(business.slug)}
            >
              <div>
                <strong>{business.name}</strong>
                <span>{[business.category?.name, business.neighborhood?.name].filter(Boolean).join(" / ") || business.slug}</span>
              </div>
              <Badge tone={statusTone(business.partnerStatus || "unclaimed")}>{business.partnerStatus || "unclaimed"}</Badge>
            </button>
          ))}
        </div>
      </section>
      <section className="panel">
        <PanelHeader title="Claim Request" />
        <form className="form-grid" onSubmit={(event) => void submit(event)}>
          <Field label="Business slug" name="businessSlug" value={selectedSlug} onChange={(value) => setSelectedSlug(value)} required />
          <Field label="Your name" name="contactName" defaultValue={state.user.name ?? ""} />
          <Field label="Your email" name="contactEmail" type="email" defaultValue={state.user.email} />
          <Field label="Your phone" name="contactPhone" defaultValue={state.user.phone ?? ""} />
          <Field label="Role" name="contactRole" placeholder="Owner, manager, operator" />
          <Field label="Business email" name="businessEmail" type="email" />
          <Field label="Business phone" name="businessPhone" />
          <TextArea label="Verification notes" name="verificationNotes" rows={5} />
          <Field label="Storefront profile URL" name="profileUrl" />
          <button className="primary-button wide" type="submit" disabled={busy}>
            {busy ? "Submitting" : "Submit claim"}
          </button>
        </form>
      </section>
    </div>
  );
}

function ListingPage({ state }: { state: AppState }) {
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    setBusy(true);
    state.setError(null);
    try {
      const values = formRecord(form);
      await api<{ ok: true }>("/partner-api/listing-requests", {
        method: "POST",
        body: values,
      });
      state.setNotice("Listing request submitted for review.");
      form.reset();
      await state.reloadDashboard();
    } catch (err) {
      state.setError(messageFromError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel constrained">
      <PanelHeader title="Get Listed" />
      <form className="form-grid two" onSubmit={(event) => void submit(event)}>
        <Select
          label="Listing type"
          name="listingType"
          options={[
            { label: "Place", value: "PLACE" },
            { label: "Service", value: "SERVICE" },
          ]}
        />
        <Field label="Listing name" name="businessName" required />
        <Select label="Category" name="category" options={state.catalog.categories.map((item) => item.name)} />
        <Select label="Neighborhood" name="neighborhood" options={state.catalog.neighborhoods.map((item) => item.name)} />
        <Field label="Address" name="address" />
        <Field label="Website" name="websiteUrl" />
        <Field label="Instagram" name="instagramUrl" />
        <Field label="TikTok" name="tiktokUrl" />
        <Field label="Your name" name="contactName" defaultValue={state.user.name ?? ""} />
        <Field label="Your email" name="contactEmail" type="email" defaultValue={state.user.email} />
        <Field label="Your phone" name="contactPhone" defaultValue={state.user.phone ?? ""} />
        <Field label="Role" name="contactRole" />
        <Field label="Evidence URL" name="evidenceUrl" />
        <TextArea label="Details" name="details" rows={5} className="span-two" />
        <button className="primary-button span-two" type="submit" disabled={busy}>
          {busy ? "Submitting" : "Submit listing"}
        </button>
      </form>
    </section>
  );
}

function BusinessesPage({ state }: { state: AppState }) {
  const hasApprovedBusiness = state.approvedBusinesses.length > 0;
  const addBusinessAction = state.summary.businesses.length ? <SmallAction label="Add another listing" path="/claim" /> : undefined;

  return (
    <div className="page-grid">
      <section className="panel">
        <PanelHeader title="Listings" action={addBusinessAction} />
        <div className="business-grid">
          {state.summary.businesses.length ? (
            state.summary.businesses.map((item) => <BusinessCardPanel key={item.roleId} item={item} />)
          ) : (
            <EmptyState
              action={hasApprovedBusiness ? undefined : "Claim from a listing profile"}
              path={hasApprovedBusiness ? undefined : "/claim"}
              text="No listings connected."
            />
          )}
        </div>
      </section>
      <ChangeRequestPage state={state} type="PROFILE_UPDATE" embedded />
    </div>
  );
}

function MediaPage({ state }: { state: AppState }) {
  const [busy, setBusy] = useState(false);
  const mediaRequests = state.summary.changeRequests.filter(isAdMediaRequest);
  const approvedMedia = mediaRequests.filter(isApprovedAdMediaRequest);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    setBusy(true);
    state.setError(null);
    try {
      await apiForm<{ ok: true }>("/partner-api/media-requests", new FormData(form));
      state.setNotice("Media submitted for approval.");
      form.reset();
      await state.reloadDashboard();
    } catch (err) {
      state.setError(messageFromError(err));
    } finally {
      setBusy(false);
    }
  }

  if (!state.approvedBusinesses.length) {
    return <section className="panel constrained"><EmptyState text="Approved business access required." /></section>;
  }

  return (
    <div className="two-column wide-left">
      <section className="panel">
        <PanelHeader title="Submit Media" />
        <div className="campaign-notice">
          <CheckCircle2 size={18} />
          <div>
            <strong>Approval required before campaigns</strong>
            <span>Upload finished creative or request So Atlanta creative production for $40. If you request production, you need to be able to provide high-quality images. Approved media becomes available on the Campaigns page.</span>
          </div>
        </div>
        <form className="form-grid two media-form" onSubmit={(event) => void submit(event)}>
          <Select
            label="Business"
            name="businessId"
            options={state.approvedBusinesses.map((item) => ({ label: item.business.name, value: item.business.id }))}
            required
          />
          <Select
            label="Placement"
            name="placement"
            options={adPlacementSpecs.map((item) => ({ label: `${item.label} (${item.size})`, value: item.value }))}
            required
          />
          <Field label="Media title" name="assetTitle" placeholder="Summer dinner promo" />
          <Field label="Link target" name="landingPageUrl" placeholder="https://example.com/menu" />
          <TextArea label="Ad copy" name="adCopy" rows={4} className="span-two" />
          <label className="field span-two">
            <span>Creative file</span>
            <input type="file" name="assetFile" accept="image/*" />
          </label>
          <label className="check-row span-two">
            <input type="checkbox" name="creativeProductionRequested" value="yes" />
            <span>Request So Atlanta creative production for $40. I can provide high-quality images for the ad.</span>
          </label>
          <button className="primary-button span-two" type="submit" disabled={busy}>
            {busy ? "Submitting" : "Submit media for approval"}
          </button>
        </form>
      </section>

      <section className="panel">
        <PanelHeader title="Approved Media" />
        <MediaRequestList items={approvedMedia} emptyText="No approved media yet." />
      </section>

      <section className="panel span-two-panel">
        <PanelHeader title="Media Review Queue" />
        <MediaRequestList items={mediaRequests} emptyText="No media submitted yet." />
      </section>
    </div>
  );
}

function CampaignsPage({ state }: { state: AppState }) {
  const [tab, setTab] = useState<"buy" | "requests">("buy");
  const [marketplace, setMarketplace] = useState<MarketplaceState | null>(null);
  const [cart, setCart] = useState<CampaignCartItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [businessId, setBusinessId] = useState(state.approvedBusinesses[0]?.business.id ?? "");
  const [featuredWeeks, setFeaturedWeeks] = useState("1");
  const [featuredStartDate, setFeaturedStartDate] = useState("");
  const [packagingMonth, setPackagingMonth] = useState("");
  const [packagingMediaId, setPackagingMediaId] = useState("");
  const [cpmType, setCpmType] = useState<"HOMEPAGE_MIDPAGE_CPM" | "BUSINESS_PROFILE_CPM">("HOMEPAGE_MIDPAGE_CPM");
  const [cpmMediaId, setCpmMediaId] = useState("");
  const [budgetInput, setBudgetInput] = useState("5.00");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
  const [targetStartHour, setTargetStartHour] = useState(9);
  const [targetEndHour, setTargetEndHour] = useState(17);

  const approvedMedia = state.summary.changeRequests.filter(isApprovedAdMediaRequest);
  const adRequests = state.summary.changeRequests.filter(isAdProductRequest);
  const selectedBusiness = state.approvedBusinesses.find((item) => item.business.id === businessId)?.business;
  const businessRule = marketplace?.businessRules.find((item) => item.businessId === businessId);
  const cartTotal = cart.reduce((sum, item) => sum + item.costCents, 0);
  const availableCredits = marketplace?.adAccount.availableCents ?? state.summary.adAccount.availableCents;
  const cpmCartCount = cart.filter((item) => item.businessId === businessId && (item.productType === "HOMEPAGE_MIDPAGE_CPM" || item.productType === "BUSINESS_PROFILE_CPM")).length;
  const cpmRemaining = marketplace
    ? Math.max(0, marketplace.settings.cpmMaxConcurrentCampaigns - (businessRule?.cpmRequestCount ?? 0) - cpmCartCount)
    : 0;

  useEffect(() => {
    api<MarketplaceState>("/partner-api/marketplace")
      .then((payload) => {
        setMarketplace(payload);
        const firstFeatured = payload.inventory.featuredWeeks.find((week) => week.spotsLeft > 0);
        const firstPackaging = payload.inventory.packagingMonths.find((month) => month.spotsLeft > 0);
        setFeaturedStartDate((current) => current || firstFeatured?.startDate || "");
        setPackagingMonth((current) => current || firstPackaging?.month || "");
      })
      .catch((err) => state.setError(messageFromError(err)));
  }, [state.setError]);

  useEffect(() => {
    if (!businessId && state.approvedBusinesses[0]) {
      setBusinessId(state.approvedBusinesses[0].business.id);
    }
  }, [businessId, state.approvedBusinesses]);

  useEffect(() => {
    setCpmMediaId("");
  }, [cpmType]);

  function mediaForPlacement(placement: string): ChangeRequest[] {
    return approvedMedia.filter((item) => payloadText(item.payload, "placement") === placement);
  }

  function featuredStartOptions(): Array<{ startDate: string; label: string; spotsLeft: number }> {
    if (!marketplace) return [];
    const weeks = Number.parseInt(featuredWeeks, 10);
    const ownedWeeks = new Set(businessRule?.featuredWeekStartDates ?? []);
    return marketplace.inventory.featuredWeeks
      .map((week, index) => {
        const segment = marketplace.inventory.featuredWeeks.slice(index, index + weeks);
        const available = segment.length === weeks && segment.every((item) => item.spotsLeft > 0 && !ownedWeeks.has(item.startDate));
        return available ? { startDate: week.startDate, label: `${formatDateKey(week.startDate)} / ${week.spotsLeft} spots left`, spotsLeft: week.spotsLeft } : null;
      })
      .filter((item): item is { startDate: string; label: string; spotsLeft: number } => Boolean(item));
  }

  function packagingOptions(): Array<{ month: string; label: string }> {
    if (!marketplace) return [];
    const ownedMonths = new Set(businessRule?.packagingMonths ?? []);
    return marketplace.inventory.packagingMonths
      .filter((month) => month.spotsLeft > 0 && !ownedMonths.has(month.month))
      .map((month) => ({ month: month.month, label: `${formatMonthKey(month.month)} / ${month.spotsLeft} spots left` }));
  }

  function addCartItem(item: Omit<CampaignCartItem, "localId">): void {
    setCart((current) => [...current, { ...item, localId: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${current.length}` }]);
  }

  function addFeatured(): void {
    if (!marketplace || !selectedBusiness || !featuredStartDate) return;
    const weeks = Number.parseInt(featuredWeeks, 10);
    const costCents = marketplace.settings.featuredWeeklyRateCents * weeks;
    addCartItem({
      productType: "FEATURED_BUSINESS",
      businessId,
      businessName: selectedBusiness.name,
      title: campaignProductLabels.FEATURED_BUSINESS,
      costCents,
      startDate: featuredStartDate,
      weeks,
      details: [
        `${weeks} week${weeks === 1 ? "" : "s"} starting ${formatDateKey(featuredStartDate)}`,
        "Includes directory/search featuring and email list placement.",
      ],
    });
  }

  function addPackaging(): void {
    if (!marketplace || !selectedBusiness || !packagingMonth || !packagingMediaId) return;
    addCartItem({
      productType: "PACKAGING_INSERT",
      businessId,
      businessName: selectedBusiness.name,
      title: campaignProductLabels.PACKAGING_INSERT,
      costCents: marketplace.settings.packagingMonthlyRateCents,
      month: packagingMonth,
      approvedMediaRequestId: packagingMediaId,
      details: [
        `${formatMonthKey(packagingMonth)} packaging card`,
        "One calendar month, while spots last.",
      ],
    });
  }

  function addCpm(): void {
    if (!marketplace || !selectedBusiness || !cpmMediaId) return;
    const budgetCents = centsFromDollarInput(budgetInput);
    if (budgetCents < marketplace.settings.cpmMinBudgetCents) {
      state.setError(`Minimum CPM budget is ${money(marketplace.settings.cpmMinBudgetCents)}.`);
      return;
    }
    if (endDate < startDate) {
      state.setError("End date must be after the start date.");
      return;
    }
    if (cpmRemaining <= 0) {
      state.setError("This business is already at the CPM campaign limit.");
      return;
    }
    const cpmCents = cpmType === "HOMEPAGE_MIDPAGE_CPM"
      ? marketplace.settings.homepageMidpageCpmCents
      : marketplace.settings.businessPageCpmCents;
    const lower = Math.min(targetStartHour, targetEndHour);
    const upper = Math.max(targetStartHour, targetEndHour);
    const estimatedImpressions = Math.floor((budgetCents / cpmCents) * 1000);
    addCartItem({
      productType: cpmType,
      businessId,
      businessName: selectedBusiness.name,
      title: campaignProductLabels[cpmType],
      costCents: budgetCents,
      budgetCents,
      startDate,
      endDate,
      approvedMediaRequestId: cpmMediaId,
      targetStartHour: lower,
      targetEndHour: upper,
      details: [
        `${money(cpmCents)} CPM / about ${estimatedImpressions.toLocaleString()} impressions`,
        `${formatDateKey(startDate)} to ${formatDateKey(endDate)}`,
        `${formatHour(lower)} - ${formatHour(upper)}`,
      ],
    });
  }

  async function submitCart(): Promise<void> {
    if (!cart.length) return;
    setBusy(true);
    state.setError(null);
    try {
      await api<{ ok: true }>("/partner-api/campaign-cart", {
        method: "POST",
        body: {
          items: cart.map((item) => ({
            productType: item.productType,
            businessId: item.businessId,
            startDate: item.startDate,
            weeks: item.weeks,
            month: item.month,
            approvedMediaRequestId: item.approvedMediaRequestId,
            budgetCents: item.budgetCents,
            endDate: item.endDate,
            targetStartHour: item.targetStartHour,
            targetEndHour: item.targetEndHour,
          })),
        },
      });
      setCart([]);
      setTab("requests");
      state.setNotice("Ad requests submitted for review.");
      await state.reloadDashboard();
      const refreshed = await api<MarketplaceState>("/partner-api/marketplace");
      setMarketplace(refreshed);
    } catch (err) {
      state.setError(messageFromError(err));
    } finally {
      setBusy(false);
    }
  }

  if (!state.approvedBusinesses.length) {
    return <section className="panel constrained"><EmptyState text="Approved business access required." /></section>;
  }

  const featuredOptions = featuredStartOptions();
  const packagingMonthOptions = packagingOptions();
  const packagingMedia = mediaForPlacement("packaging_insert");
  const cpmPlacement = cpmPlacementForProduct[cpmType];
  const cpmMedia = mediaForPlacement(cpmPlacement);
  const cpmRate = marketplace
    ? cpmType === "HOMEPAGE_MIDPAGE_CPM"
      ? marketplace.settings.homepageMidpageCpmCents
      : marketplace.settings.businessPageCpmCents
    : 0;
  const budgetCents = centsFromDollarInput(budgetInput);
  const estimatedImpressions = cpmRate ? Math.floor((budgetCents / cpmRate) * 1000) : 0;

  return (
    <div className="campaign-workspace">
      {shouldShowSetupChecklist(state) ? <SetupChecklist state={state} /> : null}

      <div className="tabs">
        <button className={tab === "buy" ? "selected" : ""} type="button" onClick={() => setTab("buy")}>Buy Ads</button>
        <button className={tab === "requests" ? "selected" : ""} type="button" onClick={() => setTab("requests")}>Submitted Requests</button>
      </div>

      {tab === "requests" ? (
        <section className="panel">
          <PanelHeader title="Submitted Ad Requests" />
          <CampaignRequestList items={adRequests} />
        </section>
      ) : (
        <div className="campaign-buy-grid">
          <section className="panel campaign-products">
            <PanelHeader title="Buy Ads" />
            <label className="field">
              <span>Business</span>
              <select value={businessId} onChange={(event) => setBusinessId(event.target.value)}>
                {state.approvedBusinesses.map((item) => (
                  <option key={item.business.id} value={item.business.id}>{item.business.name}</option>
                ))}
              </select>
            </label>

            <article className="product-panel">
              <div className="product-panel-head">
                <div>
                  <span className="label-with-tooltip">
                    <strong>Featured Business</strong>
                    <Tooltip text="A flat weekly feature for approved businesses. It includes featured placement and email list inclusion for the selected week." />
                  </span>
                  <span>{marketplace ? `${money(marketplace.settings.featuredWeeklyRateCents)} / week` : "Loading rate"}</span>
                </div>
                <CalendarDays size={20} />
              </div>
              <div className="form-grid two">
                <label className="field">
                  <span>Weeks</span>
                  <select value={featuredWeeks} onChange={(event) => setFeaturedWeeks(event.target.value)}>
                    <option value="1">1 week</option>
                    <option value="2">2 consecutive weeks</option>
                    <option value="3">3 consecutive weeks</option>
                  </select>
                </label>
                <label className="field">
                  <span className="label-with-tooltip">
                    Start Monday
                    <Tooltip text="Featured weeks start on available Mondays so the business gets the full week purchased." />
                  </span>
                  <select value={featuredStartDate} onChange={(event) => setFeaturedStartDate(event.target.value)}>
                    <option value="">Select</option>
                    {featuredOptions.map((option) => (
                      <option key={option.startDate} value={option.startDate}>{option.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <button className="secondary-button" type="button" onClick={addFeatured} disabled={!marketplace || !featuredStartDate}>
                <Plus size={16} />
                Add Featured
              </button>
            </article>

            <article className="product-panel">
              <div className="product-panel-head">
                <div>
                  <span className="label-with-tooltip">
                    <strong>CPM Placement</strong>
                    <Tooltip text="CPM means cost per 1,000 ad impressions. Your budget is converted into estimated views at the current rate." />
                  </span>
                  <span>{cpmRate ? `${money(cpmRate)} CPM current estimate` : "Loading rate"}</span>
                </div>
                <BadgeDollarSign size={20} />
              </div>
              <div className="form-grid two">
                <label className="field">
                  <span>Placement</span>
                  <select value={cpmType} onChange={(event) => setCpmType(event.target.value as "HOMEPAGE_MIDPAGE_CPM" | "BUSINESS_PROFILE_CPM")}>
                    <option value="HOMEPAGE_MIDPAGE_CPM">Homepage mid-page ad</option>
                    <option value="BUSINESS_PROFILE_CPM">Business profile page ad</option>
                  </select>
                </label>
                <label className="field">
                  <span className="label-with-tooltip">
                    Approved media
                    <Tooltip text="Only media approved for the selected placement appears here. Submit new creative on the Media page first." />
                  </span>
                  <select value={cpmMediaId} onChange={(event) => setCpmMediaId(event.target.value)}>
                    <option value="">Select</option>
                    {cpmMedia.map((item) => (
                      <option key={item.id} value={item.id}>{mediaRequestTitle(item)}</option>
                    ))}
                  </select>
                </label>
                <Field
                  label="Budget"
                  name="cpmBudget"
                  value={budgetInput}
                  onChange={setBudgetInput}
                  helpText="This amount is prepaid from ad credits and held when the ad request is submitted for review."
                />
                <Field
                  label="Start date"
                  name="cpmStartDate"
                  type="date"
                  value={startDate}
                  onChange={setStartDate}
                  helpText="Campaign delivery can begin after approval and available credits."
                />
                <Field
                  label="End date"
                  name="cpmEndDate"
                  type="date"
                  value={endDate}
                  onChange={setEndDate}
                  helpText="Use the end date to limit how long this prepaid CPM placement can spend."
                />
                <div className="campaign-estimate">
                  <span className="label-with-tooltip">
                    Estimated reach
                    <Tooltip text="Estimated impressions are calculated as budget divided by the current CPM rate, then multiplied by 1,000." />
                  </span>
                  <strong>{estimatedImpressions.toLocaleString()} impressions</strong>
                </div>
                <ControlledHourRange
                  startHour={targetStartHour}
                  endHour={targetEndHour}
                  onStart={setTargetStartHour}
                  onEnd={setTargetEndHour}
                />
              </div>
              {!cpmMedia.length ? <p className="fine-print">Submit approved {placementLabel(cpmPlacement)} media before adding this placement.</p> : null}
              <button className="secondary-button" type="button" onClick={addCpm} disabled={!marketplace || !cpmMediaId || cpmRemaining <= 0}>
                <Plus size={16} />
                Add CPM
              </button>
            </article>

            <article className="product-panel">
              <div className="product-panel-head">
                <div>
                  <span className="label-with-tooltip">
                    <strong>Packaging Insert</strong>
                    <Tooltip text="A monthly printed insert placement. Spots are limited and media must be approved for packaging before it can run." />
                  </span>
                  <span>{marketplace ? `${money(marketplace.settings.packagingMonthlyRateCents)} / month` : "Loading rate"}</span>
                </div>
                <PackageCheck size={20} />
              </div>
              <div className="form-grid two">
                <label className="field">
                  <span>Month</span>
                  <select value={packagingMonth} onChange={(event) => setPackagingMonth(event.target.value)}>
                    <option value="">Select</option>
                    {packagingMonthOptions.map((option) => (
                      <option key={option.month} value={option.month}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="label-with-tooltip">
                    Approved packaging media
                    <Tooltip text="Packaging insert creative uses its own approved size and cannot use homepage or profile ad media." />
                  </span>
                  <select value={packagingMediaId} onChange={(event) => setPackagingMediaId(event.target.value)}>
                    <option value="">Select</option>
                    {packagingMedia.map((item) => (
                      <option key={item.id} value={item.id}>{mediaRequestTitle(item)}</option>
                    ))}
                  </select>
                </label>
              </div>
              {!packagingMedia.length ? <p className="fine-print">Submit approved packaging insert media before adding this placement.</p> : null}
              <button className="secondary-button" type="button" onClick={addPackaging} disabled={!marketplace || !packagingMonth || !packagingMediaId}>
                <Plus size={16} />
                Add Packaging
              </button>
            </article>
          </section>

          <aside className="panel campaign-cart">
            <PanelHeader title="Cart" />
            <div className="credit-strip">
              <div>
                <span>Available credits</span>
                <strong>{money(availableCredits)}</strong>
              </div>
              <div>
                <span>Cart total</span>
                <strong>{money(cartTotal)}</strong>
              </div>
            </div>
            {cart.length ? (
              <div className="cart-list">
                {cart.map((item) => (
                  <article className="cart-item" key={item.localId}>
                    <div>
                      <strong>{item.title}</strong>
                      <span>{item.businessName}</span>
                      {item.details.map((detail) => <em key={detail}>{detail}</em>)}
                    </div>
                    <div>
                      <b>{money(item.costCents)}</b>
                      <button type="button" onClick={() => setCart((current) => current.filter((entry) => entry.localId !== item.localId))} title="Remove">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState text="Add an ad product to build a submission." />
            )}
            {cartTotal > availableCredits ? (
              <div className="cart-warning">
                <span>Credits are too low for this cart.</span>
                <button type="button" onClick={() => pushPath("/billing")}>Fund account</button>
              </div>
            ) : null}
            <button className="primary-button wide" type="button" onClick={() => void submitCart()} disabled={!cart.length || cartTotal > availableCredits || busy}>
              {busy ? "Submitting" : "Submit cart for review"}
            </button>
          </aside>
        </div>
      )}
    </div>
  );
}

function ChangeRequestPage({
  state,
  type,
  embedded = false,
}: {
  state: AppState;
  type: ChangeRequest["requestType"];
  embedded?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [pricing, setPricing] = useState<PricingSlot[]>([]);
  const approved = state.approvedBusinesses;
  const approvedMedia = state.summary.changeRequests.filter(isApprovedAdMediaRequest);
  const needsApprovedMedia = type === "CAMPAIGN" && !approvedMedia.length;
  const title = embedded ? "Profile Updates" : `${typeLabel(type)} Request`;
  const setError = state.setError;

  useEffect(() => {
    if (type !== "CAMPAIGN") return;
    api<{ slots: PricingSlot[] }>("/partner-api/pricing?placement=business_page&hours=24")
      .then((payload) => setPricing(payload.slots))
      .catch((err) => setError(messageFromError(err)));
  }, [setError, type]);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    setBusy(true);
    state.setError(null);
    try {
      const values = formRecord(form);
      const { businessId, ...payload } = values;
      await api<{ ok: true }>("/partner-api/change-requests", {
        method: "POST",
        body: {
          businessId,
          requestType: type,
          payload,
        },
      });
      state.setNotice(`${typeLabel(type)} request submitted for review.`);
      form.reset();
      await state.reloadDashboard();
    } catch (err) {
      state.setError(messageFromError(err));
    } finally {
      setBusy(false);
    }
  }

  const content = (
    <>
      <PanelHeader title={title} />
      {approved.length ? (
        <>
          {type === "CAMPAIGN" ? <PricingPreview slots={pricing} /> : null}
          {needsApprovedMedia ? (
            <EmptyState action="Submit media" path="/media" text="Approved media is required before a campaign can start." />
          ) : (
            <form className="form-grid two" onSubmit={(event) => void submit(event)}>
              <Select
                label="Business"
                name="businessId"
                options={approved.map((item) => ({ label: item.business.name, value: item.business.id }))}
                required
              />
              <RequestTypeFields type={type} state={state} approvedMedia={approvedMedia} />
              <button className="primary-button span-two" type="submit" disabled={busy}>
                {busy ? "Submitting" : "Submit request"}
              </button>
            </form>
          )}
        </>
      ) : (
        <EmptyState text="Approved business access required." />
      )}
    </>
  );

  return embedded ? <section className="panel">{content}</section> : <section className="panel constrained">{content}</section>;
}

function PricingPreview({ slots }: { slots: PricingSlot[] }) {
  if (!slots.length) return null;
  const visible = slots.slice(0, 8);
  return (
    <div className="pricing-preview">
      <div className="pricing-preview-header">
        <strong>Current rate</strong>
        <span>Prepaid CPM</span>
      </div>
      <div className="pricing-grid">
        {visible.map((slot) => (
          <div className="pricing-cell" key={slot.startsAt}>
            <span>{new Date(slot.startsAt).toLocaleTimeString([], { hour: "numeric" })}</span>
            <strong>{money(slot.cpmCents)} CPM</strong>
            <em>{slot.inventory}</em>
          </div>
        ))}
      </div>
    </div>
  );
}

function RequestTypeFields({
  type,
  state,
  approvedMedia = [],
}: {
  type: ChangeRequest["requestType"];
  state: AppState;
  approvedMedia?: ChangeRequest[];
}) {
  if (type === "PROFILE_UPDATE") {
    return (
      <>
        <Field label="Phone" name="phone" />
        <Field label="Website" name="websiteUrl" />
        <Field label="Instagram" name="instagramUrl" />
        <Field label="Hours" name="hoursText" />
        <TextArea label="Profile changes" name="profileNotes" rows={5} className="span-two" />
      </>
    );
  }

  if (type === "PRODUCT_LINK") {
    return (
      <>
        <Field label="Product title" name="productTitle" required />
        <Field label="Shopify handle" name="productHandle" required />
        <Field label="Collection handle" name="collectionHandle" />
        <Field label="Sort order" name="sortOrder" type="number" />
        <TextArea label="Product notes" name="productNotes" rows={4} className="span-two" />
      </>
    );
  }

  if (type === "CAMPAIGN") {
    return (
      <>
        <CampaignApprovalNotice />
      </>
    );
  }

  return (
    <>
      <Field label="Offer title" name="offerTitle" required />
      <Field label="Discount code" name="discountCode" required />
      <Field label="Staff PIN" name="staffPin" required />
      <Field label="End date" name="endDate" type="date" />
      <TextArea label="Offer terms" name="offerTerms" rows={4} className="span-two" />
      <TextArea label="Redemption notes" name="redemptionNotes" rows={4} className="span-two" />
    </>
  );
}

function CampaignApprovalNotice() {
  return (
    <div className="campaign-notice span-two">
      <CheckCircle2 size={18} />
      <div>
        <strong>Creative approval required</strong>
        <span>Campaigns can only use media that was approved on the Media page. Campaigns start after creative review and prepaid funding. Ad credits are non-refundable.</span>
      </div>
    </div>
  );
}

function CampaignHourRange() {
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(17);
  const lower = Math.min(startHour, endHour);
  const upper = Math.max(startHour, endHour);
  const rangeLabel = `${formatHour(lower)} - ${formatHour(upper)}`;

  return (
    <div className="hour-range span-two">
      <input type="hidden" name="targetStartHour" value={lower} readOnly />
      <input type="hidden" name="targetEndHour" value={upper} readOnly />
      <input type="hidden" name="targetHourRange" value={rangeLabel} readOnly />
      <div className="hour-range-header">
        <span>Target hour range</span>
        <strong>{rangeLabel}</strong>
      </div>
      <HourRangeSlider startHour={startHour} endHour={endHour} onStart={setStartHour} onEnd={setEndHour} />
    </div>
  );
}

function ControlledHourRange({
  startHour,
  endHour,
  onStart,
  onEnd,
}: {
  startHour: number;
  endHour: number;
  onStart: (value: number) => void;
  onEnd: (value: number) => void;
}) {
  const lower = Math.min(startHour, endHour);
  const upper = Math.max(startHour, endHour);
  return (
    <div className="hour-range span-two">
      <div className="hour-range-header">
        <span>Target hours</span>
        <strong>{formatHour(lower)} - {formatHour(upper)}</strong>
      </div>
      <HourRangeSlider startHour={startHour} endHour={endHour} onStart={onStart} onEnd={onEnd} />
    </div>
  );
}

function HourRangeSlider({
  startHour,
  endHour,
  onStart,
  onEnd,
}: {
  startHour: number;
  endHour: number;
  onStart: (value: number) => void;
  onEnd: (value: number) => void;
}) {
  const lower = Math.min(startHour, endHour);
  const upper = Math.max(startHour, endHour);
  const lowerPercent = (lower / 23) * 100;
  const upperPercent = (upper / 23) * 100;
  const activeInput = startHour <= endHour ? "start" : "end";

  return (
    <div
      className="range-slider"
      style={{
        "--range-start": `${lowerPercent}%`,
        "--range-end": `${upperPercent}%`,
        "--start-position": `${(startHour / 23) * 100}%`,
        "--end-position": `${(endHour / 23) * 100}%`,
      } as React.CSSProperties}
    >
      <div className="range-slider-track" aria-hidden="true">
        <span className="range-slider-fill" />
      </div>
      <input
        aria-label="Start hour"
        className={`range-input ${activeInput === "start" ? "is-active" : ""}`}
        type="range"
        min="0"
        max="23"
        value={startHour}
        onChange={(event) => onStart(Number(event.currentTarget.value))}
      />
      <input
        aria-label="End hour"
        className={`range-input ${activeInput === "end" ? "is-active" : ""}`}
        type="range"
        min="0"
        max="23"
        value={endHour}
        onChange={(event) => onEnd(Number(event.currentTarget.value))}
      />
      <span className="range-hour-label range-hour-label-start">{formatCompactHour(startHour)}</span>
      <span className="range-hour-label range-hour-label-end">{formatCompactHour(endHour)}</span>
    </div>
  );
}

function Tooltip({ text }: { text: string }) {
  return (
    <span className="tooltip" tabIndex={0} aria-label={text}>
      <InfoIcon size={14} aria-hidden="true" />
      <span className="tooltip-bubble" role="tooltip">{text}</span>
    </span>
  );
}

function BillingPage({ state }: { state: AppState }) {
  const account = state.summary.adAccount;
  const [amount, setAmount] = useState("50");
  const [busy, setBusy] = useState(false);

  async function startCheckout(): Promise<void> {
    const amountCents = Math.round(Number(amount) * 100);
    if (!Number.isFinite(amountCents) || amountCents < 500) {
      state.setError("Choose at least $5 in ad credits.");
      return;
    }
    setBusy(true);
    state.setError(null);
    try {
      const payload = await api<{ checkoutUrl: string }>("/partner-api/billing/checkout-session", {
        method: "POST",
        body: { amountCents },
      });
      window.location.href = payload.checkoutUrl;
    } catch (err) {
      state.setError(messageFromError(err));
      setBusy(false);
    }
  }

  return (
    <div className="two-column">
      <section className="panel">
        <PanelHeader title="Ad Account Funding" action={<SmallAction label="Set budget" path="/campaigns" />} />
        <div className="billing-total">{money(account.availableCents)}</div>
        <p className="fine-print flush-top">Available ad credits.</p>
        <p className="billing-policy">Ad credits are non-refundable.</p>
        <div className="billing-row">
          <span>Total balance</span>
          <strong>{money(account.balanceCents)}</strong>
        </div>
        <div className="billing-row">
          <span>Held for review</span>
          <strong>{money(account.heldCents)}</strong>
        </div>
        <div className="billing-row">
          <span>Committed campaigns</span>
          <strong>{money(account.committedCents)}</strong>
        </div>
        <div className="billing-row">
          <span>Prepaid funding</span>
          <Badge tone="good">Stripe Checkout</Badge>
        </div>
        <div className="billing-row">
          <span>Card payments</span>
          <Badge tone="good">Connected</Badge>
        </div>
        <div className="billing-row">
          <span>Campaign delivery</span>
          <Badge tone="pending">Manual approval</Badge>
        </div>
        <div className="billing-actions">
          <label className="field">
            <span>Add credits</span>
            <select value={amount} onChange={(event) => setAmount(event.target.value)}>
              <option value="25">$25</option>
              <option value="50">$50</option>
              <option value="100">$100</option>
              <option value="250">$250</option>
              <option value="500">$500</option>
            </select>
          </label>
          <button className="primary-button" type="button" onClick={() => void startCheckout()} disabled={busy}>
            <CircleDollarSign size={16} />
            {busy ? "Opening Checkout" : "Buy ad credits"}
          </button>
          <button className="primary-button" type="button" onClick={() => pushPath("/campaigns")}>
            <BadgeDollarSign size={16} />
            Set ad budget
          </button>
        </div>
      </section>
      <section className="panel">
        <PanelHeader title="Funding Queue" />
        <EmptyState action="Request campaign" path="/campaigns" text="No prepaid funding requests yet." />
      </section>
    </div>
  );
}

function SettingsPage({ state }: { state: AppState }) {
  return (
    <section className="panel constrained">
      <PanelHeader title="Account" />
      <div className="settings-list">
        <Info label="Name" value={state.user.name || "Not provided"} />
        <Info label="Email" value={state.user.email} />
        <Info label="Phone" value={state.user.phone || "Not provided"} />
        <Info label="Status" value={state.user.status} />
        <Info label="Created" value={formatDate(state.user.createdAt)} />
        <Info label="Last login" value={formatDate(state.user.lastLoginAt)} />
      </div>
    </section>
  );
}

function BrandLockup({ label, variant = "sidebar" }: { label: string; variant?: "sidebar" | "auth" }) {
  return (
    <div className={`brand ${variant === "auth" ? "auth-brand" : ""}`}>
      <span className="brand-logo-card">
        <img className="brand-logo" src={BRAND_LOGO_SRC} alt="So Atlanta" />
      </span>
      <span className="brand-context">{label}</span>
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  required = false,
  placeholder,
  autoComplete,
  defaultValue,
  value,
  onChange,
  helpText,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  helpText?: string;
}) {
  return (
    <label className="field">
      <span className={helpText ? "label-with-tooltip" : undefined}>
        {label}
        {helpText ? <Tooltip text={helpText} /> : null}
      </span>
      <input
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        defaultValue={value === undefined ? defaultValue : undefined}
        value={value}
        onChange={onChange ? (event) => onChange(event.target.value) : undefined}
      />
    </label>
  );
}

function TextArea({
  label,
  name,
  rows,
  className,
}: {
  label: string;
  name: string;
  rows: number;
  className?: string;
}) {
  return (
    <label className={`field ${className ?? ""}`}>
      <span>{label}</span>
      <textarea name={name} rows={rows} />
    </label>
  );
}

function Select({
  label,
  name,
  options,
  required = false,
}: {
  label: string;
  name: string;
  options: Array<string | { label: string; value: string }>;
  required?: boolean;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <select name={name} required={required} defaultValue="">
        <option value="" disabled={required}>
          Select
        </option>
        {options.map((option) => {
          const value = typeof option === "string" ? option : option.value;
          const optionLabel = typeof option === "string" ? option : option.label;
          return (
            <option key={value} value={value}>
              {optionLabel}
            </option>
          );
        })}
      </select>
    </label>
  );
}

function PanelHeader({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="panel-header">
      <h2>{title}</h2>
      {action}
    </div>
  );
}

function Metric({ label, value, icon }: { label: string; value: string; icon: ReactNode }) {
  return (
    <div className="metric">
      <div>{icon}</div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function BusinessRow({ item }: { item: BusinessSummary }) {
  return (
    <div className="table-row">
      <div>
        <strong>{item.business.name}</strong>
        <span>{[listingTypeLabel(item.business.listingType), item.business.category?.name, item.business.neighborhood?.name].filter(Boolean).join(" / ") || item.business.slug}</span>
      </div>
      <Badge tone={statusTone(item.status)}>{item.status}</Badge>
    </div>
  );
}

function BusinessCardPanel({ item }: { item: BusinessSummary }) {
  return (
    <article className="business-card">
      <div className="business-image">
        {item.business.logoUrl || item.business.imageUrl ? <img src={item.business.logoUrl || item.business.imageUrl || ""} alt="" /> : <Building2 size={28} />}
      </div>
      <div>
        <h3>{item.business.name}</h3>
        <p>{[item.business.category?.name, item.business.neighborhood?.name].filter(Boolean).join(" / ") || item.business.slug}</p>
        <Badge tone="pending">{listingTypeLabel(item.business.listingType)}</Badge>
      </div>
      <div className="business-actions">
        <Badge tone={statusTone(item.status)}>{item.status}</Badge>
        {item.business.shopifyUrl ? (
          <a href={item.business.shopifyUrl} target="_blank" rel="noreferrer">
            Storefront
          </a>
        ) : null}
      </div>
    </article>
  );
}

function RequestList({ items }: { items: Array<{ id: string; title: string; type: string; status: string; date: string }> }) {
  if (!items.length) {
    return <EmptyState text="No requests yet." />;
  }
  return (
    <div className="table-list">
      {items.map((item) => (
        <div className="table-row" key={`${item.type}-${item.id}`}>
          <div>
            <strong>{item.title}</strong>
            <span>
              {item.type} / {formatDate(item.date)}
            </span>
          </div>
          <Badge tone={statusTone(item.status)}>{item.status}</Badge>
        </div>
      ))}
    </div>
  );
}

function CampaignRequestList({ items }: { items: ChangeRequest[] }) {
  if (!items.length) {
    return <EmptyState text="No ad requests submitted yet." />;
  }
  return (
    <div className="table-list campaign-request-list">
      {items.map((item) => {
        const payload = payloadRecord(item.payload);
        const holdStatus = payloadText(payload, "creditHoldStatus");
        const costCents = typeof payload.costCents === "number" ? payload.costCents : typeof payload.budgetCents === "number" ? payload.budgetCents : 0;
        return (
          <article className="table-row campaign-request-row" key={item.id}>
            <div>
              <strong>{requestProductTitle(item)}</strong>
              <span>{item.business?.name ?? "Business"} / {requestProductDetails(item)}</span>
              {holdStatus ? <em>Credits: {holdStatus.toLowerCase()}</em> : null}
            </div>
            <div className="request-row-side">
              {costCents ? <b>{money(costCents)}</b> : null}
              <Badge tone={statusTone(item.status)}>{item.status}</Badge>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function MediaRequestList({ items, emptyText }: { items: ChangeRequest[]; emptyText: string }) {
  if (!items.length) {
    return <EmptyState text={emptyText} />;
  }

  return (
    <div className="media-list">
      {items.map((item) => {
        const payload = payloadRecord(item.payload);
        const assetUrl = payloadText(payload, "assetUrl");
        const placement = payloadText(payload, "placement");
        const size = [payload.requiredWidth, payload.requiredHeight].filter(Boolean).join(" x ");
        const productionRequested = payload.creativeProductionRequested === true;
        return (
          <article className="media-item" key={item.id}>
            <div className="media-thumb">
              {assetUrl ? <img src={assetUrl} alt="" loading="lazy" /> : <ImageIcon size={22} />}
            </div>
            <div className="media-item-body">
              <div className="media-item-head">
                <strong>{mediaRequestTitle(item)}</strong>
                <Badge tone={statusTone(item.status)}>{item.status}</Badge>
              </div>
              <span>{placementLabel(placement)}{size ? ` / ${size}` : ""}</span>
              {productionRequested ? <em>$40 creative production requested. High-quality images required.</em> : null}
              {payloadText(payload, "landingPageUrl") ? <a href={payloadText(payload, "landingPageUrl")} target="_blank" rel="noopener noreferrer">Open link target</a> : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function SmallAction({ label, path }: { label: string; path: string }) {
  return (
    <button className="link-button" type="button" onClick={() => pushPath(path)}>
      {label}
      <ChevronRight size={14} />
    </button>
  );
}

function EmptyState({ text, action, path }: { text: string; action?: string; path?: string }) {
  return (
    <div className="empty-state">
      <p>{text}</p>
      {action && path ? (
        <button type="button" onClick={() => pushPath(path)}>
          {action}
        </button>
      ) : null}
    </div>
  );
}

function Badge({ children, tone }: { children: ReactNode; tone: string }) {
  return <span className={`badge ${tone}`}>{children}</span>;
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="info-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Banner({ children, tone, onClose }: { children: ReactNode; tone: "success" | "error"; onClose: () => void }) {
  return (
    <div className={`banner ${tone}`}>
      <span>{children}</span>
      <button type="button" onClick={onClose}>
        Dismiss
      </button>
    </div>
  );
}

function ScreenCenter({ children }: { children: ReactNode }) {
  return <div className="screen-center">{children}</div>;
}

function typeLabel(type: ChangeRequest["requestType"]): string {
  if (type === "PROFILE_UPDATE") return "Profile update";
  if (type === "PRODUCT_LINK") return "Product";
  if (type === "CAMPAIGN") return "Campaign";
  return "Offer";
}

createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
